import React from 'react';
import { Box, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { AlertProps } from '@mui/material/Alert';
import { useParams } from 'react-router-dom';
import { WorkshopStatistics, WorkshopInterfaceStatistics, getWorkshopStatistics } from '../types/Workshop';

import { Modal } from './Modal';
import { useSnackbar } from '../../contexts/SnackBarContext';

import { DataGrid, GridColDef } from '@mui/x-data-grid';

export const WorkshopStatisticsModal = () => {
    const { id: wsid } = useParams();
    const { showSnackbar } = useSnackbar();
    const [workshopStatistics, setWorkshopStatistics] = React.useState<WorkshopStatistics | null>(null);
    const [workshopIfaceStatistics, setWorkshopIfaceStatistics] = React.useState<WorkshopInterfaceStatistics[] >([]);

    const showUserFeedback = React.useCallback((msg: string | null, severity: AlertProps['severity'] = 'error') => {
        msg && showSnackbar(msg, severity);
    }, [showSnackbar]);

    React.useEffect(() => {
        const fetch = async () => {
            try {

                let wsStatsReponse = await getWorkshopStatistics(Number(wsid), new AbortController());

                if (wsStatsReponse.failed) {
                    showUserFeedback(wsStatsReponse.errorText);
                }

                setWorkshopStatistics(wsStatsReponse.result);
                setWorkshopIfaceStatistics(wsStatsReponse.result.interfaceStatistics);

            } catch (error) {
                showUserFeedback(String(error));
            }
        }

        fetch();

    }, [wsid, showUserFeedback]);

    const submitEventHandler = () =>
        (e: { preventDefault: () => void; }) => {
            e.preventDefault();
        }

    const columns: GridColDef[] = [
        {
            field: 'id',
            headerName: 'ID',
            flex: 1,
            valueGetter: (params, row) => row.datagridId
        },
        {
            field: 'interfaceName',
            headerName: 'Interface',
            flex: 3
        },
        {
            field: 'totalTestcases',
            headerName: 'Total',
            flex: 1,
        },
        {
            field: 'executedTestcases',
            headerName: 'Executed',
            flex: 1
        },
        {
            field: 'failedTestcases',
            headerName: 'Failed',
            flex: 1
        }
    ];

    return <Modal title={"Workshop Stats"}>
        <Typography variant={'h5'} sx={{ marginBottom: 20 }}>
            Here are some interesting statistics about the workshop.
        </Typography>

        <form onSubmit={submitEventHandler()}>
            <Grid container rowSpacing={2} columnSpacing={30}>
                <Grid container size={{ xs: 5 }}>
                    <Grid size={{ xs: 8 }} textAlign="right">
                        <Typography variant={'body2'} sx={{ marginBottom: 20 }}>
                            {`Workshop Interfaces:`}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                        <Typography variant={'body2'} sx={{ marginBottom: 20 }}>
                            {`${workshopStatistics?.interfacesUsed ?? 0} / ${workshopStatistics?.availableInterfaces ?? 0}`}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 8 }} textAlign="right">
                        <Typography variant={'body2'} sx={{ marginBottom: 20 }}>
                            {`Registered Users:`}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }} >
                        <Typography variant={'body2'} sx={{ marginBottom: 20 }}>
                            {`${workshopStatistics?.registeredUsers ?? 0}`}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 8 }} textAlign="right">
                        <Typography variant={'body2'} sx={{ marginBottom: 20 }}>
                            {`Registered Companies:`}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }} >
                        <Typography variant={'body2'} sx={{ marginBottom: 20 }}>
                            {`${workshopStatistics?.registeredCompanies ?? 0}`}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 8 }} textAlign="right">
                        <Typography variant={'body2'} sx={{ marginBottom: 20 }}>
                            {`Registered Products:`}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                        <Typography variant={'body2'} sx={{ marginBottom: 20 }}>
                            {`${workshopStatistics?.registeredProducts ?? 0}`}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 8 }} textAlign="right">
                        <Typography variant={'body2'} sx={{ marginBottom: 20 }}>
                            {`Available Features:`}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                        <Typography variant={'body2'} sx={{ marginBottom: 20 }}>
                            {`${workshopStatistics?.validCombinations ?? 0}`}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 8 }} textAlign="right">
                        <Typography variant={'body2'} sx={{ marginBottom: 20 }}>
                            {`Total Tests Expected:`}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                        <Typography variant={'body2'} sx={{ marginBottom: 20 }}>
                            {`${workshopStatistics?.totalTestcases ?? 0}`}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 8 }} textAlign="right">
                        <Typography variant={'body2'} sx={{ marginBottom: 20 }}>
                            {`Total Tests Completed:`}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                        <Typography variant={'body2'} sx={{ marginBottom: 20 }}>
                            {`${workshopStatistics?.executedTestcases ?? 0}`}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 8 }} textAlign="right">
                        <Typography variant={'body2'} sx={{ marginBottom: 20 }}>
                            {`Total Failed Tests:`}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                        <Typography variant={'body2'} sx={{ marginBottom: 20 }}>
                            {`${workshopStatistics?.failedTestcases ?? 0}`}
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container size={{ xs: 7 }}>
                    <Box sx={{ width: '100%' }} marginTop={15} maxHeight="300px" overflow="auto" >
                        <DataGrid
                            columns={columns}
                            rows={workshopIfaceStatistics}
                            columnVisibilityModel={{ id: false }}
                            hideFooter
                        />
                    </Box>
                </Grid>
            </Grid>
        </form>
    </Modal>
}
